import * as React from 'react';
import { Loader } from 'Src/components/Loader/Loader';
import { superAdminGet, superAdminPost, superAdminPut, superAdminDelete } from 'Src/helpers/service/superadmin/api';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ArrangementCheckbox {
  text: string;
  key: string;
  isRequired: boolean;
  defaultState: boolean;
}

interface Arrangement {
  id: string;
  title?: string;
  text?: string;
  type?: string;
  affects?: string;
  affectsEverybodyBeforeDate?: string;
  visibleSince?: string;
  blockingSince?: string;
  checkboxes?: ArrangementCheckbox[];
  createdAt?: string;
}

interface ClientSignature {
  id: string;
  createdAt?: string;
  remoteAddress?: string;
  userAgent?: string;
  user?: { firstName?: string; lastName?: string; email?: string };
}

interface CheckboxDraft {
  text: string;
  key: string;
  isRequired: boolean;
  defaultState: boolean;
}

interface ArrangementForm {
  title: string;
  text: string;
  type: string;
  affects: string;
  affectsEverybodyBeforeDate: string;
  visibleSince: string;
  blockingSince: string;
  checkboxes: CheckboxDraft[];
}

const EMPTY_FORM: ArrangementForm = {
  title: '',
  text: '',
  type: 'AGREEMENT',
  affects: 'EVERYBODY',
  affectsEverybodyBeforeDate: '',
  visibleSince: '',
  blockingSince: '',
  checkboxes: []
};

const EMPTY_CHECKBOX: CheckboxDraft = { text: '', key: '', isRequired: false, defaultState: false };

// ─── API ─────────────────────────────────────────────────────────────────────

function getArrangements(skip = 0): Promise<Arrangement[]> {
  return superAdminGet('/arrangements', { params: { filter: { limit: 50, skip, order: 'createdAt DESC' } } });
}

function createArrangement(data: any): Promise<Arrangement> {
  return superAdminPost('/arrangements', data);
}

function updateArrangement(id: string, data: any): Promise<Arrangement> {
  return superAdminPut(`/arrangements/${id}`, data);
}

function deleteArrangement(id: string): Promise<void> {
  return superAdminDelete(`/arrangements/${id}`);
}

function getClientSignatures(arrangementId: string, skip = 0): Promise<ClientSignature[]> {
  return superAdminGet(`/arrangements/${arrangementId}/signature`, {
    params: { filter: { limit: 50, skip, order: 'createdAt DESC' } }
  });
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function toDateTimeLocal(iso?: string): string {
  if (!iso) return '';
  return iso.slice(0, 16);
}

function toISO(local: string): string | undefined {
  if (!local) return undefined;
  return new Date(local).toISOString();
}

function formatDateTime(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function arrangementToForm(a: Arrangement): ArrangementForm {
  return {
    title: a.title || '',
    text: a.text || '',
    type: a.type || 'AGREEMENT',
    affects: a.affects || 'EVERYBODY',
    affectsEverybodyBeforeDate: toDateTimeLocal(a.affectsEverybodyBeforeDate),
    visibleSince: toDateTimeLocal(a.visibleSince),
    blockingSince: toDateTimeLocal(a.blockingSince),
    checkboxes: (a.checkboxes || []).map(c => ({ ...c }))
  };
}

function buildPayload(form: ArrangementForm): any {
  const data: any = {
    title: form.title,
    text: form.text,
    type: form.type,
    affects: form.affects,
    checkboxes: form.checkboxes.filter(c => c.key && c.text)
  };
  if (form.visibleSince) data.visibleSince = toISO(form.visibleSince);
  if (form.blockingSince) data.blockingSince = toISO(form.blockingSince);
  if (form.affects === 'EVERYBODY_BEFORE_DATE' && form.affectsEverybodyBeforeDate) {
    data.affectsEverybodyBeforeDate = toISO(form.affectsEverybodyBeforeDate);
  }
  return data;
}

// ─── Component ───────────────────────────────────────────────────────────────

type ViewMode = 'list' | 'form' | 'signatures';

interface Props {}

interface State {
  isLoading: boolean;
  isSaving: boolean;
  arrangements: Arrangement[];
  currentPage: number;
  hasMore: boolean;
  errorMessage: string;
  successMessage: string;
  viewMode: ViewMode;
  editingId: string | null;
  form: ArrangementForm;
  signatures: ClientSignature[];
  signaturesArrangement: Arrangement | null;
  signaturesPage: number;
  signaturesHasMore: boolean;
  isLoadingSignatures: boolean;
}

export class SuperAdminArrangements extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: false,
      isSaving: false,
      arrangements: [],
      currentPage: 0,
      hasMore: false,
      errorMessage: '',
      successMessage: '',
      viewMode: 'list',
      editingId: null,
      form: { ...EMPTY_FORM },
      signatures: [],
      signaturesArrangement: null,
      signaturesPage: 0,
      signaturesHasMore: false,
      isLoadingSignatures: false
    };
  }

  componentDidMount() {
    this.load(0);
  }

  load = (page: number) => {
    this.setState({ isLoading: true, errorMessage: '' });
    getArrangements(page * 50)
      .then(arrangements =>
        this.setState({ isLoading: false, arrangements, currentPage: page, hasMore: arrangements.length === 50 })
      )
      .catch(() => this.setState({ isLoading: false, errorMessage: 'Could not load arrangements.' }));
  };

  onNewClick = () => {
    this.setState({ viewMode: 'form', editingId: null, form: { ...EMPTY_FORM }, errorMessage: '', successMessage: '' });
  };

  onEditClick = (a: Arrangement) => {
    this.setState({
      viewMode: 'form',
      editingId: a.id,
      form: arrangementToForm(a),
      errorMessage: '',
      successMessage: ''
    });
  };

  onDeleteClick = (a: Arrangement) => {
    if (!window.confirm(`Delete arrangement "${a.title}"?`)) return;
    this.setState({ isLoading: true });
    deleteArrangement(a.id)
      .then(() => {
        this.setState({ successMessage: 'Deleted.' });
        this.load(this.state.currentPage);
      })
      .catch(() => this.setState({ isLoading: false, errorMessage: 'Could not delete.' }));
  };

  onViewSignaturesClick = (a: Arrangement) => {
    this.setState({
      viewMode: 'signatures',
      signaturesArrangement: a,
      signatures: [],
      signaturesPage: 0,
      isLoadingSignatures: true
    });
    getClientSignatures(a.id, 0)
      .then(signatures =>
        this.setState({ signatures, isLoadingSignatures: false, signaturesHasMore: signatures.length === 50 })
      )
      .catch(() => this.setState({ isLoadingSignatures: false }));
  };

  onBackClick = () => {
    this.setState({ viewMode: 'list', errorMessage: '', successMessage: '' });
    this.load(this.state.currentPage);
  };

  onFormChange = (field: keyof ArrangementForm, value: any) => {
    this.setState(prev => ({ form: { ...prev.form, [field]: value } }));
  };

  onCheckboxChange = (index: number, field: keyof CheckboxDraft, value: any) => {
    this.setState(prev => ({
      form: {
        ...prev.form,
        checkboxes: prev.form.checkboxes.map((c, i) => (i === index ? { ...c, [field]: value } : c))
      }
    }));
  };

  onAddCheckbox = () => {
    this.setState(prev => ({ form: { ...prev.form, checkboxes: [...prev.form.checkboxes, { ...EMPTY_CHECKBOX }] } }));
  };

  onRemoveCheckbox = (index: number) => {
    this.setState(prev => ({ form: { ...prev.form, checkboxes: prev.form.checkboxes.filter((_, i) => i !== index) } }));
  };

  onSubmit = () => {
    const { form, editingId } = this.state;
    if (!form.title.trim()) {
      this.setState({ errorMessage: 'Title is required.' });
      return;
    }
    if (!form.text.trim()) {
      this.setState({ errorMessage: 'Text is required.' });
      return;
    }
    if (form.affects === 'EVERYBODY_BEFORE_DATE' && !form.affectsEverybodyBeforeDate) {
      this.setState({ errorMessage: 'Please set the "Affects everybody before date".' });
      return;
    }

    this.setState({ isSaving: true, errorMessage: '' });
    const payload = buildPayload(form);
    const request = editingId ? updateArrangement(editingId, payload) : createArrangement(payload);

    request
      .then(() => {
        this.setState({ isSaving: false, viewMode: 'list', successMessage: editingId ? 'Updated.' : 'Created.' });
        this.load(0);
      })
      .catch(() => this.setState({ isSaving: false, errorMessage: 'Could not save. Please check fields.' }));
  };

  renderList() {
    const { isLoading, arrangements, currentPage, hasMore, errorMessage, successMessage } = this.state;

    if (isLoading) return <Loader />;

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="text-muted small">
            {arrangements.length} arrangement{arrangements.length !== 1 ? 's' : ''}
          </div>
          <button className="btn btn-primary btn-sm" onClick={this.onNewClick}>
            + New arrangement
          </button>
        </div>

        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
        {successMessage && <div className="alert alert-success">{successMessage}</div>}

        {arrangements.length === 0 ? (
          <div className="text-muted">No arrangements found.</div>
        ) : (
          <>
            <table className="table table-sm table-bordered table-hover">
              <thead className="thead-light">
                <tr>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Affects</th>
                  <th>Visible since</th>
                  <th>Blocking since</th>
                  <th>Checkboxes</th>
                  <th style={{ width: 190 }}></th>
                </tr>
              </thead>
              <tbody>
                {arrangements.map(a => (
                  <tr key={a.id}>
                    <td className="small font-weight-bold">{a.title}</td>
                    <td>
                      <span className={`badge ${a.type === 'AGREEMENT' ? 'badge-primary' : 'badge-info'}`}>
                        {a.type}
                      </span>
                    </td>
                    <td className="small">
                      {a.affects === 'EVERYBODY_BEFORE_DATE'
                        ? `Before ${formatDateTime(a.affectsEverybodyBeforeDate)}`
                        : 'Everybody'}
                    </td>
                    <td className="small">{formatDateTime(a.visibleSince)}</td>
                    <td className="small">{formatDateTime(a.blockingSince)}</td>
                    <td className="text-center small">{(a.checkboxes || []).length}</td>
                    <td>
                      <div className="d-flex" style={{ gap: 4 }}>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => this.onViewSignaturesClick(a)}
                        >
                          Signatures
                        </button>
                        <button className="btn btn-sm btn-outline-primary" onClick={() => this.onEditClick(a)}>
                          Edit
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => this.onDeleteClick(a)}>
                          Del
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="d-flex" style={{ gap: 8 }}>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => this.load(currentPage - 1)}
                disabled={currentPage === 0}
              >
                ← Previous
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => this.load(currentPage + 1)}
                disabled={!hasMore}
              >
                Next →
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  renderForm() {
    const { form, isSaving, editingId, errorMessage } = this.state;

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">{editingId ? 'Edit arrangement' : 'New arrangement'}</h5>
          <button className="btn btn-outline-secondary btn-sm" onClick={this.onBackClick} disabled={isSaving}>
            ← Back
          </button>
        </div>

        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}

        <div className="card">
          <div className="card-body">
            <div className="form-row">
              <div className="col-md-6 form-group">
                <label className="small font-weight-bold">
                  Title <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={form.title}
                  onChange={e => this.onFormChange('title', e.target.value)}
                  disabled={isSaving}
                />
              </div>
              <div className="col-md-3 form-group">
                <label className="small font-weight-bold">Type</label>
                <select
                  className="form-control form-control-sm"
                  value={form.type}
                  onChange={e => this.onFormChange('type', e.target.value)}
                  disabled={isSaving}
                >
                  <option value="AGREEMENT">Agreement</option>
                  <option value="NOTIFICATION">Notification</option>
                </select>
              </div>
              <div className="col-md-3 form-group">
                <label className="small font-weight-bold">Affects</label>
                <select
                  className="form-control form-control-sm"
                  value={form.affects}
                  onChange={e => this.onFormChange('affects', e.target.value)}
                  disabled={isSaving}
                >
                  <option value="EVERYBODY">Everybody</option>
                  <option value="EVERYBODY_BEFORE_DATE">Everybody before date</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="small font-weight-bold">
                Text <span className="text-danger">*</span>
              </label>
              <textarea
                className="form-control form-control-sm"
                rows={5}
                value={form.text}
                onChange={e => this.onFormChange('text', e.target.value)}
                disabled={isSaving}
              />
            </div>

            <div className="form-row">
              <div className="col-md-4 form-group">
                <label className="small font-weight-bold">Visible since</label>
                <input
                  type="datetime-local"
                  className="form-control form-control-sm"
                  value={form.visibleSince}
                  onChange={e => this.onFormChange('visibleSince', e.target.value)}
                  disabled={isSaving}
                />
              </div>
              <div className="col-md-4 form-group">
                <label className="small font-weight-bold">Blocking since</label>
                <input
                  type="datetime-local"
                  className="form-control form-control-sm"
                  value={form.blockingSince}
                  onChange={e => this.onFormChange('blockingSince', e.target.value)}
                  disabled={isSaving}
                />
              </div>
              {form.affects === 'EVERYBODY_BEFORE_DATE' && (
                <div className="col-md-4 form-group">
                  <label className="small font-weight-bold">
                    Affects everybody before <span className="text-danger">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    className="form-control form-control-sm"
                    value={form.affectsEverybodyBeforeDate}
                    onChange={e => this.onFormChange('affectsEverybodyBeforeDate', e.target.value)}
                    disabled={isSaving}
                  />
                </div>
              )}
            </div>

            <div className="mt-2">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="small font-weight-bold mb-0">Checkboxes</label>
                <button className="btn btn-outline-secondary btn-sm" onClick={this.onAddCheckbox} disabled={isSaving}>
                  + Add checkbox
                </button>
              </div>
              {form.checkboxes.map((cb, i) => (
                <div key={i} className="border rounded p-3 mb-2 bg-light">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong className="small">Checkbox #{i + 1}</strong>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => this.onRemoveCheckbox(i)}
                      disabled={isSaving}
                    >
                      ✕
                    </button>
                  </div>
                  <div className="form-row">
                    <div className="col-md-5 form-group mb-0">
                      <label className="small">
                        Text <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={cb.text}
                        onChange={e => this.onCheckboxChange(i, 'text', e.target.value)}
                        disabled={isSaving}
                      />
                    </div>
                    <div className="col-md-3 form-group mb-0">
                      <label className="small">
                        Key <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={cb.key}
                        onChange={e => this.onCheckboxChange(i, 'key', e.target.value)}
                        disabled={isSaving}
                        placeholder="unique_key"
                      />
                    </div>
                    <div className="col-md-2 d-flex align-items-end pb-1">
                      <label className="mb-0 small" style={{ cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          className="mr-1"
                          checked={cb.isRequired}
                          onChange={e => this.onCheckboxChange(i, 'isRequired', e.target.checked)}
                          disabled={isSaving}
                        />
                        Required
                      </label>
                    </div>
                    <div className="col-md-2 d-flex align-items-end pb-1">
                      <label className="mb-0 small" style={{ cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          className="mr-1"
                          checked={cb.defaultState}
                          onChange={e => this.onCheckboxChange(i, 'defaultState', e.target.checked)}
                          disabled={isSaving}
                        />
                        Default on
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-3">
              <button className="btn btn-primary btn-sm mr-2" onClick={this.onSubmit} disabled={isSaving}>
                {isSaving ? 'Saving…' : editingId ? 'Save changes' : 'Create'}
              </button>
              <button className="btn btn-outline-secondary btn-sm" onClick={this.onBackClick} disabled={isSaving}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderSignatures() {
    const { signaturesArrangement, signatures, isLoadingSignatures, signaturesPage, signaturesHasMore } = this.state;

    const loadSigs = (page: number) => {
      this.setState({ isLoadingSignatures: true });
      getClientSignatures(signaturesArrangement!.id, page * 50)
        .then(sigs =>
          this.setState({
            signatures: sigs,
            signaturesPage: page,
            signaturesHasMore: sigs.length === 50,
            isLoadingSignatures: false
          })
        )
        .catch(() => this.setState({ isLoadingSignatures: false }));
    };

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="mb-0">Client signatures</h5>
            <div className="text-muted small">"{signaturesArrangement?.title}"</div>
          </div>
          <button className="btn btn-outline-secondary btn-sm" onClick={this.onBackClick}>
            ← Back
          </button>
        </div>

        {isLoadingSignatures ? (
          <Loader />
        ) : signatures.length === 0 ? (
          <div className="text-muted">No signatures yet.</div>
        ) : (
          <>
            <table className="table table-sm table-bordered table-hover">
              <thead className="thead-light">
                <tr>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>IP</th>
                  <th>User agent</th>
                </tr>
              </thead>
              <tbody>
                {signatures.map(s => (
                  <tr key={s.id}>
                    <td className="small">{formatDateTime(s.createdAt)}</td>
                    <td className="small">{[s.user?.firstName, s.user?.lastName].filter(Boolean).join(' ') || '—'}</td>
                    <td className="small">{s.user?.email || '—'}</td>
                    <td className="small">{s.remoteAddress || '—'}</td>
                    <td
                      className="small"
                      style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={s.userAgent}
                    >
                      {s.userAgent || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="d-flex" style={{ gap: 8 }}>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => loadSigs(signaturesPage - 1)}
                disabled={signaturesPage === 0}
              >
                ← Previous
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => loadSigs(signaturesPage + 1)}
                disabled={!signaturesHasMore}
              >
                Next →
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  render() {
    const { viewMode } = this.state;
    return (
      <div>
        <div className="mb-4">
          <h4 className="mb-1">Arrangements</h4>
          <div className="text-muted small">Manage user agreements and notifications shown on login.</div>
        </div>
        {viewMode === 'list' && this.renderList()}
        {viewMode === 'form' && this.renderForm()}
        {viewMode === 'signatures' && this.renderSignatures()}
      </div>
    );
  }
}
